import { acceptedFiles } from 'modules/contestacao/contestacao.model';
import { AiEngine } from 'services/ai-engine/ai-engine.model';
import { ApiKeyListItem } from 'services/api-key/api-key.model';
import { Branch } from 'services/branch/branch.model';
import { Client } from 'services/client/client.model';
import { FileType } from 'services/file-type/file-type.model';
import { Product } from 'services/product/product.model';
import {
	LimitType,
	ProductSetupBilling,
	ProductSetupDetail,
	ProductSetupStatus,
} from 'services/product-setup/product-setup.model';
import { ROLE } from 'services/role/role.model';
import { AuditUser, User, UserStatus } from 'services/user/user.model';
import { Vertical } from 'services/vertical/vertical.model';
import { MOCK_CLIENTS, PROTOTYPE_CLIENT_IDS } from 'mocks/shared/prototype-clients.const';
import {
	HubProductSlug,
	MOCK_AUDIT_USER,
	MOCK_CATALOG,
	MOCK_HUB_ROLES,
	MOCK_PEOPLE,
	MOCK_VERTICAL_INFO,
	productSetupIdOf,
} from '../shared/mock-context.const';

/**
 * Estado em memória das entidades de contexto/admin (clientes, ramos, verticais,
 * produtos, configurações de produto, usuários, chaves de API e tipos de arquivo).
 * Os arrays são mutados pelos handlers e persistem durante a sessão.
 */

const CREATED_AT = '2026-01-10T12:00:00.000Z';
const UPDATED_AT = '2026-08-20T12:00:00.000Z';

const AUDIT = {
	createdAt: CREATED_AT,
	createdBy: MOCK_AUDIT_USER,
	updatedAt: UPDATED_AT,
	updatedBy: MOCK_AUDIT_USER,
	deletedAt: null,
	deletedBy: null,
};

// ── Tipos de arquivo ──────────────────────────────────────────────────────────

/** Ids fixos: Contestação usa `acceptedFiles.INITIAL` / `SUBSIDY` diretamente no código */
export const MOCK_FILE_TYPE_IDS = {
	peticaoInicial: acceptedFiles.INITIAL as string,
	procuracao: '019440f7-fc00-7b10-8000-000000000002',
	sentenca: '019440f7-fc00-7b10-8000-000000000003',
	subsidio: acceptedFiles.SUBSIDY as string,
	contratoTrabalho: '019440f7-fc00-7b10-8000-000000000005',
	holerite: '019440f7-fc00-7b10-8000-000000000006',
	cartaoPonto: '019440f7-fc00-7b10-8000-000000000007',
	termoRescisao: '019440f7-fc00-7b10-8000-000000000008',
	atestadoMedico: '019440f7-fc00-7b10-8000-000000000009',
	documentoIdentidade: '019440f7-fc00-7b10-8000-000000000010',
	comprovanteResidencia: '019440f7-fc00-7b10-8000-000000000011',
	notaFiscal: '019440f7-fc00-7b10-8000-000000000012',
} as const;

function fileType(id: string, name: string, description: string): FileType {
	return { id, name, description, ...AUDIT };
}

export const fileTypesDb: FileType[] = [
	fileType(
		MOCK_FILE_TYPE_IDS.peticaoInicial,
		'Petição Inicial',
		'Peça inaugural do processo apresentada pelo reclamante.',
	),
	fileType(
		MOCK_FILE_TYPE_IDS.procuracao,
		'Procuração',
		'Instrumento de mandato outorgado ao advogado.',
	),
	fileType(MOCK_FILE_TYPE_IDS.sentenca, 'Sentença', 'Decisão judicial de primeira instância.'),
	fileType(
		MOCK_FILE_TYPE_IDS.subsidio,
		'Subsídio',
		'Documentos de apoio enviados pela empresa para a defesa.',
	),
	fileType(
		MOCK_FILE_TYPE_IDS.contratoTrabalho,
		'Contrato de Trabalho',
		'Contrato individual de trabalho e aditivos.',
	),
	fileType(MOCK_FILE_TYPE_IDS.holerite, 'Holerite', 'Demonstrativo de pagamento de salário.'),
	fileType(MOCK_FILE_TYPE_IDS.cartaoPonto, 'Cartão de Ponto', 'Registro de jornada de trabalho.'),
	fileType(
		MOCK_FILE_TYPE_IDS.termoRescisao,
		'Termo de Rescisão (TRCT)',
		'Termo de rescisão do contrato de trabalho.',
	),
	fileType(
		MOCK_FILE_TYPE_IDS.atestadoMedico,
		'Atestado Médico',
		'Atestados e laudos médicos apresentados pelo empregado.',
	),
	fileType(
		MOCK_FILE_TYPE_IDS.documentoIdentidade,
		'Documento de Identidade',
		'RG, CNH ou outro documento oficial com foto.',
	),
	fileType(
		MOCK_FILE_TYPE_IDS.comprovanteResidencia,
		'Comprovante de Residência',
		'Conta de consumo ou declaração de endereço.',
	),
	fileType(
		MOCK_FILE_TYPE_IDS.notaFiscal,
		'Nota Fiscal',
		'Documento fiscal de compra ou prestação de serviço.',
	),
	{
		...fileType('019440f7-fc00-7b10-8000-000000000099', 'Fax', 'Tipo descontinuado.'),
		deletedAt: '2026-05-02T14:30:00.000Z',
		deletedBy: MOCK_AUDIT_USER,
	},
];

// ── Ramos de atividade (mesmos de `MOCK_BRANCHES` das telas do protótipo) ────────

function branch(id: string, name: string, description: string): Branch {
	return { id, name, description, ...AUDIT };
}

export const branchesDb: Branch[] = [
	branch('1', 'Aviação', 'Aviação comercial e transporte aéreo de passageiros e cargas'),
	branch('2', 'Automotivo', 'Setor automotivo, montadoras e concessionárias'),
	branch('3', 'Financeiro', 'Serviços financeiros, bancos e seguradoras'),
	branch('4', 'Varejo', 'Comércio varejista e e-commerce'),
	branch('5', 'Tecnologia', 'Empresas de tecnologia e software'),
	branch('6', 'Entretenimento', 'Streaming, mídia e entretenimento digital'),
	branch('7', 'Saúde', 'Hospitais, clínicas, laboratórios e operadoras de saúde'),
	branch(
		'8',
		'Logística e Transporte',
		'Transporte rodoviário de cargas, armazenagem e distribuição',
	),
	branch('9', 'Telecomunicações', 'Operadoras de telefonia, internet e TV por assinatura'),
	branch('10', 'Construção Civil', 'Construtoras, incorporadoras e empresas de engenharia'),
	branch('11', 'Hotelaria e Turismo', 'Redes hoteleiras, resorts e operadoras de turismo'),
	branch('12', 'Educação', 'Instituições de ensino básico, superior e profissionalizante'),
	branch('13', 'Energia', 'Geração, transmissão e comercialização de energia'),
	branch('14', 'Alimentos e Agronegócio', 'Indústria de alimentos, frigoríficos e agronegócio'),
	branch('15', 'Seguros', 'Seguradoras, resseguradoras e corretoras de seguros'),
	{
		...branch('16', 'Mineração', 'Extração e beneficiamento de minérios'),
		deletedAt: '2026-06-18T16:20:00.000Z',
		deletedBy: MOCK_AUDIT_USER,
	},
];

// ── Verticais ─────────────────────────────────────────────────────────────────

const VERTICAL_DESCRIPTIONS: Record<string, string> = {
	trabalhista: 'Ações e rotinas do contencioso trabalhista.',
	tributario: 'Planejamento e contencioso tributário.',
	civel: 'Contencioso cível e consumerista.',
};

/**
 * Verticais cadastradas só no admin (fora de `MOCK_VERTICAL_INFO`): nenhuma configuração
 * de produto aponta para elas, então não afetam menu, jobs nem templates.
 */
const ADMIN_ONLY_VERTICALS: Vertical[] = [
	{
		id: 'vert-consumidor',
		name: 'Consumidor',
		slug: 'consumidor',
		description: 'Demandas consumeristas de massa e Procon.',
		metadata: { color: '#0F766E', background: '#99F6E4' },
		...AUDIT,
		createdAt: '2026-05-06T12:00:00.000Z',
	},
	{
		id: 'vert-previdenciario',
		name: 'Previdenciário',
		slug: 'previdenciario',
		description: 'Benefícios previdenciários e revisões junto ao INSS.',
		metadata: { color: '#B91C1C', background: '#FECACA' },
		...AUDIT,
		createdAt: '2026-07-14T12:00:00.000Z',
	},
	{
		id: 'vert-ambiental',
		name: 'Ambiental',
		slug: 'ambiental',
		description: 'Licenciamento e passivos ambientais.',
		metadata: { color: '#3F6212', background: '#D9F99D' },
		...AUDIT,
		createdAt: '2026-02-19T12:00:00.000Z',
		deletedAt: '2026-06-02T09:15:00.000Z',
		deletedBy: MOCK_AUDIT_USER,
	},
];

export const verticalsDb: Vertical[] = [
	...Object.entries(MOCK_VERTICAL_INFO).map(([slug, info]) => ({
		id: info.id,
		name: info.name,
		slug,
		description: VERTICAL_DESCRIPTIONS[slug] ?? '',
		metadata: { color: info.color, background: info.background },
		...AUDIT,
	})),
	...ADMIN_ONLY_VERTICALS,
];

// ── Produtos do catálogo ──────────────────────────────────────────────────────

export const productsDb: Product[] = Object.values(MOCK_CATALOG).map((product) => ({ ...product }));

// ── Motores de IA (usados nas faixas de faturamento) ─────────────────────────

function aiEngine(
	id: string,
	name: string,
	provider: string,
	modelId: string,
	input: number,
	output: number,
): AiEngine {
	return {
		id,
		name,
		description: `${name} (${provider})`,
		provider,
		modelId,
		costInputPer1kTokens: input,
		costOutputPer1kTokens: output,
		...AUDIT,
	};
}

export const aiEnginesDb: AiEngine[] = [
	aiEngine('engine-gpt4o', 'GPT-4o', 'OpenAI', 'gpt-4o', 0.005, 0.015),
	aiEngine('engine-claude', 'Claude Sonnet', 'Anthropic', 'claude-sonnet', 0.003, 0.015),
	aiEngine('engine-gemini', 'Gemini Pro', 'Google', 'gemini-pro', 0.00125, 0.005),
];

// ── Clientes ──────────────────────────────────────────────────────────────────

/** Clientes do protótipo (ids/nomes de `MOCK_CLIENTS`); `productSetups` é montado sob demanda */
export type ClientRecord = Omit<Client, 'productSetups'>;

/** Clientes desativados (soft delete) — alimentam o filtro de status do admin */
const CLIENT_DELETIONS: Record<string, string> = {
	[PROTOTYPE_CLIENT_IDS.maresol]: '2026-08-14T10:00:00.000Z',
	[PROTOTYPE_CLIENT_IDS.agrocerrado]: '2026-05-30T10:00:00.000Z',
};

/** Os 4 clientes originais mantêm as datas de criação históricas do protótipo */
const ORIGINAL_CLIENTS_COUNT = 4;

function clientFrom(index: number, createdAt: string): ClientRecord {
	const source = MOCK_CLIENTS[index];
	const branchRecord =
		branchesDb.find((candidate) => candidate.id === source.branch?.id) ?? branchesDb[0];
	const deletedAt = CLIENT_DELETIONS[source.id] ?? null;
	return {
		id: source.id,
		name: source.name,
		cnpj: source.cnpj,
		logo: source.logo ?? null,
		...AUDIT,
		createdAt,
		updatedAt:
			index < ORIGINAL_CLIENTS_COUNT ? UPDATED_AT : new Date(source.updatedAt).toISOString(),
		deletedAt,
		deletedBy: deletedAt ? MOCK_AUDIT_USER : null,
		branch: branchRecord,
	};
}

export const clientsDb: ClientRecord[] = MOCK_CLIENTS.map((source, index) =>
	clientFrom(
		index,
		index < ORIGINAL_CLIENTS_COUNT
			? new Date(Date.UTC(2025, 10 + index, 5, 13)).toISOString()
			: new Date(source.createdAt).toISOString(),
	),
);

export const ACME_CLIENT_ID = MOCK_CLIENTS[0].id;
const LATAM_CLIENT_ID = MOCK_CLIENTS[1]?.id ?? ACME_CLIENT_ID;
const NETFLIX_CLIENT_ID = MOCK_CLIENTS[2]?.id ?? ACME_CLIENT_ID;

// ── Configurações de produto (product setups) ─────────────────────────────────

export function billingTier(
	id: string,
	pageInitial: number,
	pageFinal: number | null,
	baseValue: number,
	excessChargeValue: number | null,
	engineId: string,
): ProductSetupBilling {
	return {
		id,
		pageInitial,
		pageFinal,
		baseValue,
		excessChargeValue,
		createdAt: CREATED_AT,
		updatedAt: UPDATED_AT,
		createdBy: MOCK_AUDIT_USER.id,
		updatedBy: MOCK_AUDIT_USER.id,
		aiEngine: aiEnginesDb.find((engine) => engine.id === engineId) ?? aiEnginesDb[0],
	};
}

export function setupStatus(
	setup: Pick<ProductSetupDetail, 'isEnabled' | 'hasExpirationDate' | 'expirationDate'>,
): ProductSetupStatus {
	if (!setup.isEnabled) return 'Inativo';
	if (
		setup.hasExpirationDate &&
		setup.expirationDate &&
		new Date(setup.expirationDate).getTime() < Date.now()
	) {
		return 'Vencido';
	}
	return 'Ativo';
}

interface SetupSeed {
	id: string;
	clientId: string;
	verticalSlug: string;
	product: HubProductSlug;
	isEnabled?: boolean;
	expirationDate?: string | null;
	monthlyLimit?: number | null;
	limitType?: LimitType | null;
	hasExcessCharge?: boolean;
	createdAt: string;
	webhookUrl?: string | null;
	billingTiers?: ProductSetupBilling[];
}

const TEMPLATE_DESCRIPTIONS: Record<HubProductSlug, string> = {
	cadastro: 'Extração de dados cadastrais trabalhistas',
	contestacao: 'Contestação trabalhista padrão',
	basilisk: 'Análise forense de documentos',
};

function buildSetup(seed: SetupSeed): ProductSetupDetail {
	const client = clientsDb.find((candidate) => candidate.id === seed.clientId) ?? clientsDb[0];
	const vertical =
		verticalsDb.find((candidate) => candidate.slug === seed.verticalSlug) ?? verticalsDb[0];
	const expirationDate = seed.expirationDate ?? null;
	const billings = seed.billingTiers ?? [];
	const templateId = `tpl-${seed.verticalSlug}-${seed.product}`;
	/** Inclui `metadata` (cores) para os badges de produto na listagem de clientes do admin */
	const verticalRef = { id: vertical.id, name: vertical.name, metadata: vertical.metadata };
	const customPromptId = `cprompt-${seed.id}`;
	const detail: ProductSetupDetail = {
		id: seed.id,
		productCatalogId: MOCK_CATALOG[seed.product].id,
		client: { id: client.id, name: client.name },
		productPromptTemplateId: templateId,
		productCustomPromptId: customPromptId,
		isEnabled: seed.isEnabled ?? true,
		hasExpirationDate: expirationDate !== null,
		expirationDate,
		hasMonthlyLimit: seed.monthlyLimit != null,
		monthlyLimit: seed.monthlyLimit ?? null,
		limitType: seed.limitType ?? null,
		hasExcessCharge: seed.hasExcessCharge ?? false,
		createdAt: seed.createdAt,
		createdBy: MOCK_AUDIT_USER,
		updatedAt: UPDATED_AT,
		updatedBy: MOCK_AUDIT_USER,
		productCatalog:
			productsDb.find((product) => product.slug === seed.product) ?? MOCK_CATALOG[seed.product],
		users: [],
		apiKey: null,
		promptTemplate: [
			{
				id: templateId,
				description: TEMPLATE_DESCRIPTIONS[seed.product],
				version: 3,
				status: 'PUBLISHED',
				vertical: { id: vertical.id, name: vertical.name },
				createdAt: CREATED_AT,
				updatedAt: UPDATED_AT,
			},
		],
		customPrompts: [
			{
				id: customPromptId,
				description: `${TEMPLATE_DESCRIPTIONS[seed.product]} — ${client.name}`,
				version: 2,
				createdAt: seed.createdAt,
				updatedAt: UPDATED_AT,
			},
		],
		billings,
		billingTiers: billings,
		vertical: verticalRef,
		templateInfo: { id: templateId, description: TEMPLATE_DESCRIPTIONS[seed.product], version: 3 },
		activePrompt: {
			id: customPromptId,
			version: 2,
			description: `${TEMPLATE_DESCRIPTIONS[seed.product]} — ${client.name}`,
			updatedAt: UPDATED_AT,
		},
		status: 'Ativo',
		webhookUrl: seed.webhookUrl ?? null,
		webhookHeaders: seed.webhookUrl ? { Authorization: 'Bearer ********' } : null,
		webhookResponseFormat: seed.webhookUrl ? 'JSON' : null,
	};
	detail.status = setupStatus(detail);
	return detail;
}

type PrototypeClientSlug = keyof typeof PROTOTYPE_CLIENT_IDS;

/** Id padrão das configurações dos clientes fictícios: `ps-<cliente>-<vertical>-<produto>` */
export function clientSetupIdOf(
	clientSlug: PrototypeClientSlug,
	verticalSlug: string,
	product: HubProductSlug,
): string {
	return `ps-${clientSlug}-${verticalSlug}-${product}`;
}

function clientSetup(
	clientSlug: PrototypeClientSlug,
	verticalSlug: string,
	product: HubProductSlug,
	seed: Omit<SetupSeed, 'id' | 'clientId' | 'verticalSlug' | 'product'>,
): ProductSetupDetail {
	return buildSetup({
		id: clientSetupIdOf(clientSlug, verticalSlug, product),
		clientId: PROTOTYPE_CLIENT_IDS[clientSlug],
		verticalSlug,
		product,
		...seed,
	});
}

export const productSetupsDb: ProductSetupDetail[] = [
	buildSetup({
		id: productSetupIdOf('trabalhista', 'cadastro'),
		clientId: ACME_CLIENT_ID,
		verticalSlug: 'trabalhista',
		product: 'cadastro',
		expirationDate: '2027-03-31T23:59:59.000Z',
		monthlyLimit: 5000,
		limitType: 'PAGES',
		hasExcessCharge: true,
		createdAt: '2026-01-15T13:00:00.000Z',
		webhookUrl: 'https://api.acme.com.br/webhooks/lbca/cadastro',
		billingTiers: [
			billingTier('bill-acme-cad-1', 1, 2000, 1500, 0.9, 'engine-gpt4o'),
			billingTier('bill-acme-cad-2', 2001, 5000, 3200, 0.75, 'engine-gpt4o'),
			billingTier('bill-acme-cad-3', 5001, null, 5800, 0.6, 'engine-claude'),
		],
	}),
	buildSetup({
		id: productSetupIdOf('trabalhista', 'contestacao'),
		clientId: ACME_CLIENT_ID,
		verticalSlug: 'trabalhista',
		product: 'contestacao',
		expirationDate: '2026-10-15T23:59:59.000Z',
		monthlyLimit: 300,
		limitType: 'JOBS',
		createdAt: '2026-02-03T13:00:00.000Z',
		billingTiers: [billingTier('bill-acme-con-1', 1, 300, 9000, null, 'engine-claude')],
	}),
	buildSetup({
		id: productSetupIdOf('trabalhista', 'basilisk'),
		clientId: ACME_CLIENT_ID,
		verticalSlug: 'trabalhista',
		product: 'basilisk',
		createdAt: '2026-04-22T13:00:00.000Z',
		billingTiers: [billingTier('bill-acme-bas-1', 1, null, 2500, 1.2, 'engine-gemini')],
	}),
	buildSetup({
		id: 'ps-latam-trabalhista-cadastro',
		clientId: LATAM_CLIENT_ID,
		verticalSlug: 'trabalhista',
		product: 'cadastro',
		expirationDate: '2027-01-31T23:59:59.000Z',
		monthlyLimit: 12000,
		limitType: 'PAGES',
		hasExcessCharge: true,
		createdAt: '2026-02-10T13:00:00.000Z',
		billingTiers: [billingTier('bill-latam-cad-1', 1, null, 7200, 0.55, 'engine-gpt4o')],
	}),
	buildSetup({
		id: 'ps-latam-trabalhista-contestacao',
		clientId: LATAM_CLIENT_ID,
		verticalSlug: 'trabalhista',
		product: 'contestacao',
		expirationDate: '2026-06-30T23:59:59.000Z',
		monthlyLimit: 150,
		limitType: 'JOBS',
		createdAt: '2025-12-01T13:00:00.000Z',
	}),
	buildSetup({
		id: 'ps-latam-civel-basilisk',
		clientId: LATAM_CLIENT_ID,
		verticalSlug: 'civel',
		product: 'basilisk',
		createdAt: '2026-05-18T13:00:00.000Z',
	}),
	buildSetup({
		id: 'ps-netflix-trabalhista-cadastro',
		clientId: NETFLIX_CLIENT_ID,
		verticalSlug: 'trabalhista',
		product: 'cadastro',
		isEnabled: false,
		createdAt: '2026-03-07T13:00:00.000Z',
	}),
	buildSetup({
		id: 'ps-netflix-civel-basilisk',
		clientId: NETFLIX_CLIENT_ID,
		verticalSlug: 'civel',
		product: 'basilisk',
		expirationDate: '2026-12-31T23:59:59.000Z',
		monthlyLimit: 800,
		limitType: 'PAGES',
		createdAt: '2026-06-01T13:00:00.000Z',
	}),
	// ── Clientes fictícios adicionados para dar volume ao admin ──
	clientSetup('horizonte', 'trabalhista', 'cadastro', {
		expirationDate: '2027-04-30T23:59:59.000Z',
		monthlyLimit: 8000,
		limitType: 'PAGES',
		hasExcessCharge: true,
		createdAt: '2025-10-20T13:00:00.000Z',
		webhookUrl: 'https://integracoes.redehorizonte.com.br/lbca/cadastro',
		billingTiers: [
			billingTier('bill-horizonte-cad-1', 1, 4000, 2600, 0.8, 'engine-gpt4o'),
			billingTier('bill-horizonte-cad-2', 4001, null, 4900, 0.62, 'engine-gpt4o'),
		],
	}),
	clientSetup('horizonte', 'trabalhista', 'contestacao', {
		expirationDate: '2027-06-30T23:59:59.000Z',
		monthlyLimit: 200,
		limitType: 'JOBS',
		createdAt: '2025-11-12T13:00:00.000Z',
		billingTiers: [billingTier('bill-horizonte-con-1', 1, 200, 7400, null, 'engine-claude')],
	}),
	clientSetup('horizonte', 'civel', 'basilisk', {
		createdAt: '2026-03-04T13:00:00.000Z',
		billingTiers: [billingTier('bill-horizonte-bas-1', 1, null, 1800, 1.1, 'engine-gemini')],
	}),
	clientSetup('valenorte', 'trabalhista', 'cadastro', {
		expirationDate: '2026-12-31T23:59:59.000Z',
		monthlyLimit: 3000,
		limitType: 'PAGES',
		createdAt: '2025-11-10T13:00:00.000Z',
		billingTiers: [billingTier('bill-valenorte-cad-1', 1, 3000, 2100, null, 'engine-gemini')],
	}),
	clientSetup('valenorte', 'trabalhista', 'contestacao', {
		expirationDate: '2026-08-31T23:59:59.000Z',
		monthlyLimit: 120,
		limitType: 'JOBS',
		createdAt: '2025-12-02T13:00:00.000Z',
		billingTiers: [billingTier('bill-valenorte-con-1', 1, 120, 4800, null, 'engine-claude')],
	}),
	clientSetup('aurora', 'civel', 'cadastro', {
		expirationDate: '2027-12-31T23:59:59.000Z',
		monthlyLimit: 15000,
		limitType: 'PAGES',
		hasExcessCharge: true,
		createdAt: '2025-12-15T13:00:00.000Z',
		webhookUrl: 'https://api.grupoaurora.com.br/juridico/webhooks/lbca',
		billingTiers: [
			billingTier('bill-aurora-cad-1', 1, 5000, 3500, 0.85, 'engine-gpt4o'),
			billingTier('bill-aurora-cad-2', 5001, 10000, 6400, 0.7, 'engine-gpt4o'),
			billingTier('bill-aurora-cad-3', 10001, null, 9800, 0.52, 'engine-claude'),
		],
	}),
	clientSetup('aurora', 'trabalhista', 'contestacao', {
		expirationDate: '2027-02-28T23:59:59.000Z',
		monthlyLimit: 250,
		limitType: 'JOBS',
		createdAt: '2026-01-08T13:00:00.000Z',
		billingTiers: [billingTier('bill-aurora-con-1', 1, 250, 8600, null, 'engine-claude')],
	}),
	clientSetup('aurora', 'civel', 'basilisk', {
		isEnabled: false,
		monthlyLimit: 1000,
		limitType: 'PAGES',
		createdAt: '2026-02-17T13:00:00.000Z',
	}),
	clientSetup('pampa', 'civel', 'basilisk', {
		monthlyLimit: 2000,
		limitType: 'PAGES',
		hasExcessCharge: true,
		createdAt: '2026-01-26T13:00:00.000Z',
		webhookUrl: 'https://hooks.telecompampa.com.br/ouvidoria/lbca',
		billingTiers: [billingTier('bill-pampa-bas-1', 1, 2000, 2900, 1.35, 'engine-gemini')],
	}),
	clientSetup('pampa', 'trabalhista', 'cadastro', {
		expirationDate: '2026-10-20T23:59:59.000Z',
		monthlyLimit: 1500,
		limitType: 'PAGES',
		createdAt: '2026-03-18T13:00:00.000Z',
		billingTiers: [billingTier('bill-pampa-cad-1', 1, 1500, 1200, null, 'engine-gpt4o')],
	}),
	clientSetup('serraazul', 'trabalhista', 'cadastro', {
		monthlyLimit: 2500,
		limitType: 'PAGES',
		createdAt: '2026-02-11T13:00:00.000Z',
		billingTiers: [billingTier('bill-serraazul-cad-1', 1, 2500, 1900, null, 'engine-gemini')],
	}),
	clientSetup('serraazul', 'trabalhista', 'contestacao', {
		isEnabled: false,
		monthlyLimit: 40,
		limitType: 'JOBS',
		createdAt: '2026-04-09T13:00:00.000Z',
	}),
	clientSetup('litoral', 'tributario', 'cadastro', {
		expirationDate: '2027-03-31T23:59:59.000Z',
		monthlyLimit: 6000,
		limitType: 'PAGES',
		hasExcessCharge: true,
		createdAt: '2026-03-02T13:00:00.000Z',
		billingTiers: [
			billingTier('bill-litoral-cad-1', 1, 3000, 2300, 0.75, 'engine-gemini'),
			billingTier('bill-litoral-cad-2', 3001, null, 4100, 0.6, 'engine-gemini'),
		],
	}),
	clientSetup('litoral', 'civel', 'basilisk', {
		monthlyLimit: 1500,
		limitType: 'PAGES',
		createdAt: '2026-04-15T13:00:00.000Z',
		webhookUrl: 'https://antifraude.bancolitoral.coop.br/api/v1/lbca/eventos',
		billingTiers: [billingTier('bill-litoral-bas-1', 1, 1500, 2200, null, 'engine-claude')],
	}),
	clientSetup('maresol', 'trabalhista', 'cadastro', {
		isEnabled: false,
		expirationDate: '2026-07-31T23:59:59.000Z',
		monthlyLimit: 800,
		limitType: 'PAGES',
		createdAt: '2026-03-20T13:00:00.000Z',
		billingTiers: [billingTier('bill-maresol-cad-1', 1, 800, 900, null, 'engine-gemini')],
	}),
	clientSetup('ipe', 'trabalhista', 'cadastro', {
		expirationDate: '2027-01-31T23:59:59.000Z',
		monthlyLimit: 1000,
		limitType: 'PAGES',
		createdAt: '2026-04-06T13:00:00.000Z',
		billingTiers: [billingTier('bill-ipe-cad-1', 1, 1000, 850, null, 'engine-gemini')],
	}),
	clientSetup('ipe', 'trabalhista', 'contestacao', {
		expirationDate: '2027-01-31T23:59:59.000Z',
		monthlyLimit: 60,
		limitType: 'JOBS',
		createdAt: '2026-05-11T13:00:00.000Z',
		billingTiers: [billingTier('bill-ipe-con-1', 1, 60, 2700, null, 'engine-claude')],
	}),
	clientSetup('solaris', 'tributario', 'cadastro', {
		monthlyLimit: 4000,
		limitType: 'PAGES',
		hasExcessCharge: true,
		createdAt: '2026-05-04T13:00:00.000Z',
		billingTiers: [billingTier('bill-solaris-cad-1', 1, 4000, 3000, 0.7, 'engine-gpt4o')],
	}),
	clientSetup('solaris', 'civel', 'basilisk', {
		expirationDate: '2026-09-15T23:59:59.000Z',
		monthlyLimit: 600,
		limitType: 'PAGES',
		createdAt: '2026-05-27T13:00:00.000Z',
		billingTiers: [billingTier('bill-solaris-bas-1', 1, 600, 1100, null, 'engine-gemini')],
	}),
	clientSetup('campoverde', 'trabalhista', 'cadastro', {
		expirationDate: '2027-05-31T23:59:59.000Z',
		monthlyLimit: 10000,
		limitType: 'PAGES',
		hasExcessCharge: true,
		createdAt: '2026-05-25T13:00:00.000Z',
		webhookUrl: 'https://erp.campoverde.ind.br/integracoes/lbca/cadastro',
		billingTiers: [
			billingTier('bill-campoverde-cad-1', 1, 5000, 3400, 0.8, 'engine-gpt4o'),
			billingTier('bill-campoverde-cad-2', 5001, null, 6200, 0.58, 'engine-claude'),
		],
	}),
	clientSetup('campoverde', 'trabalhista', 'contestacao', {
		expirationDate: '2027-05-31T23:59:59.000Z',
		monthlyLimit: 400,
		limitType: 'JOBS',
		hasExcessCharge: true,
		createdAt: '2026-06-03T13:00:00.000Z',
		webhookUrl: 'https://erp.campoverde.ind.br/integracoes/lbca/contestacao',
		billingTiers: [billingTier('bill-campoverde-con-1', 1, 400, 11500, 32, 'engine-claude')],
	}),
	clientSetup('campoverde', 'trabalhista', 'basilisk', {
		createdAt: '2026-07-16T13:00:00.000Z',
		billingTiers: [billingTier('bill-campoverde-bas-1', 1, null, 1600, 1.05, 'engine-gemini')],
	}),
	clientSetup('atlantica', 'civel', 'basilisk', {
		expirationDate: '2027-06-30T23:59:59.000Z',
		monthlyLimit: 5000,
		limitType: 'PAGES',
		hasExcessCharge: true,
		createdAt: '2026-06-15T13:00:00.000Z',
		webhookUrl: 'https://sinistros.seguradoraatlantica.com.br/webhooks/lbca',
		billingTiers: [
			billingTier('bill-atlantica-bas-1', 1, 2500, 3900, 1.25, 'engine-claude'),
			billingTier('bill-atlantica-bas-2', 2501, null, 6800, 0.95, 'engine-claude'),
		],
	}),
	clientSetup('atlantica', 'civel', 'cadastro', {
		monthlyLimit: 7000,
		limitType: 'PAGES',
		createdAt: '2026-07-02T13:00:00.000Z',
		billingTiers: [billingTier('bill-atlantica-cad-1', 1, 7000, 4700, null, 'engine-gpt4o')],
	}),
	clientSetup('rotasul', 'trabalhista', 'cadastro', {
		expirationDate: '2027-07-31T23:59:59.000Z',
		monthlyLimit: 1500,
		limitType: 'PAGES',
		createdAt: '2026-07-08T13:00:00.000Z',
		billingTiers: [billingTier('bill-rotasul-cad-1', 1, 1500, 1250, null, 'engine-gemini')],
	}),
	clientSetup('agrocerrado', 'tributario', 'cadastro', {
		isEnabled: false,
		monthlyLimit: 500,
		limitType: 'PAGES',
		createdAt: '2025-10-09T13:00:00.000Z',
	}),
];

// ── Usuários ──────────────────────────────────────────────────────────────────

/** Vínculo usuário × configuração de produto, com o role naquela configuração */
export interface UserContextSeed {
	productSetupId: string;
	roleId: string;
}

/** Usuário guardado em memória; `contexts` é expandido para `AdminUserContext` na resposta */
export interface UserRecord extends Omit<User, 'contexts'> {
	contexts: UserContextSeed[];
}

const ACME_ALL = [
	productSetupIdOf('trabalhista', 'cadastro'),
	productSetupIdOf('trabalhista', 'contestacao'),
	productSetupIdOf('trabalhista', 'basilisk'),
];

function contexts(productSetupIds: string[], role: ROLE): UserContextSeed[] {
	return productSetupIds.map((productSetupId) => ({
		productSetupId,
		roleId: MOCK_HUB_ROLES[role].id,
	}));
}

function user(
	person: AuditUser,
	status: UserStatus,
	createdAt: string,
	userContexts: UserContextSeed[],
	extra: Partial<UserRecord> = {},
): UserRecord {
	return {
		...person,
		lockedAt: null,
		status,
		isAdmin: false,
		createdAt,
		createdBy: MOCK_AUDIT_USER,
		updatedAt: UPDATED_AT,
		updatedBy: MOCK_AUDIT_USER,
		contexts: userContexts,
		...extra,
	};
}

export const usersDb: UserRecord[] = [
	user(
		MOCK_PEOPLE.admin,
		'ACTIVE',
		'2025-11-03T12:00:00.000Z',
		contexts(ACME_ALL, ROLE.BUSINESS_USER),
		{
			isAdmin: true,
		},
	),
	user(
		MOCK_PEOPLE.gerente,
		'ACTIVE',
		'2025-12-01T12:00:00.000Z',
		contexts(ACME_ALL, ROLE.BUSINESS_USER),
	),
	user(
		MOCK_PEOPLE.operador,
		'ACTIVE',
		'2026-01-20T12:00:00.000Z',
		contexts([ACME_ALL[0]], ROLE.OPERATOR),
	),
	user(
		MOCK_PEOPLE.juliana,
		'ACTIVE',
		'2026-02-11T12:00:00.000Z',
		contexts([ACME_ALL[1]], ROLE.OPERATOR),
	),
	user(
		{ id: 'usr-005', name: 'Pedro Almeida', email: 'pedro.almeida@lbca.com.br', image: null },
		'ACTIVE',
		'2026-03-02T12:00:00.000Z',
		[],
	),
	user(
		{
			id: 'usr-006',
			name: 'Marcos Vinícius Souza',
			email: 'marcos.souza@acme.com.br',
			image: null,
		},
		'BLOCKED',
		'2026-03-15T12:00:00.000Z',
		contexts([ACME_ALL[0], ACME_ALL[2]], ROLE.OPERATOR),
		{ lockedAt: '2026-09-10T18:42:00.000Z' },
	),
	user(
		{
			id: 'usr-007',
			name: 'Fernanda Oliveira',
			email: 'fernanda.oliveira@acme.com.br',
			image: null,
		},
		'INACTIVE',
		'2026-09-18T12:00:00.000Z',
		contexts([ACME_ALL[1]], ROLE.OPERATOR),
	),
	user(
		{ id: 'usr-008', name: 'Rafael Martins', email: 'rafael.martins@acme.com.br', image: null },
		'ACTIVE',
		'2026-04-08T12:00:00.000Z',
		contexts([ACME_ALL[2]], ROLE.OPERATOR),
	),
	user(
		{ id: 'usr-009', name: 'Beatriz Santos', email: 'beatriz.santos@latam.com', image: null },
		'ACTIVE',
		'2026-02-12T12:00:00.000Z',
		contexts(
			['ps-latam-trabalhista-cadastro', 'ps-latam-trabalhista-contestacao'],
			ROLE.BUSINESS_USER,
		),
	),
	user(
		{ id: 'usr-010', name: 'Thiago Rocha', email: 'thiago.rocha@acme.com.br', image: null },
		'REMOVED',
		'2026-01-25T12:00:00.000Z',
		[],
		{ deletedAt: '2026-07-01T15:00:00.000Z', deletedBy: MOCK_AUDIT_USER },
	),
	// ── Usuários dos clientes fictícios ──
	user(
		{
			id: 'usr-011',
			name: 'Mariana Lopes',
			email: 'mariana.lopes@redehorizonte.com.br',
			image: null,
		},
		'ACTIVE',
		'2025-10-22T12:00:00.000Z',
		contexts(
			[
				clientSetupIdOf('horizonte', 'trabalhista', 'cadastro'),
				clientSetupIdOf('horizonte', 'trabalhista', 'contestacao'),
				clientSetupIdOf('horizonte', 'civel', 'basilisk'),
			],
			ROLE.BUSINESS_USER,
		),
	),
	user(
		{
			id: 'usr-012',
			name: 'Gustavo Henrique Prado',
			email: 'gustavo.prado@redehorizonte.com.br',
			image: null,
		},
		'ACTIVE',
		'2025-11-04T12:00:00.000Z',
		contexts([clientSetupIdOf('horizonte', 'trabalhista', 'cadastro')], ROLE.OPERATOR),
	),
	user(
		{
			id: 'usr-013',
			name: 'Letícia Andrade',
			email: 'leticia.andrade@redehorizonte.com.br',
			image: null,
		},
		'INACTIVE',
		'2026-09-22T12:00:00.000Z',
		contexts([clientSetupIdOf('horizonte', 'trabalhista', 'contestacao')], ROLE.OPERATOR),
	),
	user(
		{
			id: 'usr-014',
			name: 'Rodrigo Tavares',
			email: 'rodrigo.tavares@valenorte.com.br',
			image: null,
		},
		'ACTIVE',
		'2025-11-12T12:00:00.000Z',
		contexts(
			[
				clientSetupIdOf('valenorte', 'trabalhista', 'cadastro'),
				clientSetupIdOf('valenorte', 'trabalhista', 'contestacao'),
			],
			ROLE.BUSINESS_USER,
		),
	),
	user(
		{
			id: 'usr-015',
			name: 'Camila Nogueira',
			email: 'camila.nogueira@valenorte.com.br',
			image: null,
		},
		'BLOCKED',
		'2025-12-03T12:00:00.000Z',
		contexts([clientSetupIdOf('valenorte', 'trabalhista', 'cadastro')], ROLE.OPERATOR),
		{ lockedAt: '2026-09-19T11:07:00.000Z' },
	),
	user(
		{
			id: 'usr-016',
			name: 'Felipe Carvalho',
			email: 'felipe.carvalho@grupoaurora.com.br',
			image: null,
		},
		'ACTIVE',
		'2025-12-16T12:00:00.000Z',
		contexts(
			[
				clientSetupIdOf('aurora', 'civel', 'cadastro'),
				clientSetupIdOf('aurora', 'trabalhista', 'contestacao'),
				clientSetupIdOf('aurora', 'civel', 'basilisk'),
			],
			ROLE.BUSINESS_USER,
		),
	),
	user(
		{
			id: 'usr-017',
			name: 'Isabela Moreira',
			email: 'isabela.moreira@grupoaurora.com.br',
			image: null,
		},
		'ACTIVE',
		'2026-01-05T12:00:00.000Z',
		contexts([clientSetupIdOf('aurora', 'civel', 'cadastro')], ROLE.OPERATOR),
	),
	user(
		{
			id: 'usr-018',
			name: 'Bruno Azevedo',
			email: 'bruno.azevedo@grupoaurora.com.br',
			image: null,
		},
		'ACTIVE',
		'2026-01-09T12:00:00.000Z',
		contexts(
			[
				clientSetupIdOf('aurora', 'trabalhista', 'contestacao'),
				clientSetupIdOf('aurora', 'civel', 'cadastro'),
			],
			ROLE.OPERATOR,
		),
	),
	user(
		{
			id: 'usr-019',
			name: 'Patrícia Mendes',
			email: 'patricia.mendes@telecompampa.com.br',
			image: null,
		},
		'ACTIVE',
		'2026-01-27T12:00:00.000Z',
		[
			...contexts([clientSetupIdOf('pampa', 'civel', 'basilisk')], ROLE.BUSINESS_USER),
			...contexts([clientSetupIdOf('pampa', 'trabalhista', 'cadastro')], ROLE.OPERATOR),
		],
	),
	user(
		{
			id: 'usr-020',
			name: 'Lucas Farias',
			email: 'lucas.farias@telecompampa.com.br',
			image: null,
		},
		'REMOVED',
		'2026-02-02T12:00:00.000Z',
		[],
		{ deletedAt: '2026-08-05T14:20:00.000Z', deletedBy: MOCK_AUDIT_USER },
	),
	user(
		{
			id: 'usr-021',
			name: 'Renata Batista',
			email: 'renata.batista@serraazul.eng.br',
			image: null,
		},
		'ACTIVE',
		'2026-02-12T12:00:00.000Z',
		[
			...contexts([clientSetupIdOf('serraazul', 'trabalhista', 'cadastro')], ROLE.BUSINESS_USER),
			...contexts([clientSetupIdOf('serraazul', 'trabalhista', 'contestacao')], ROLE.OPERATOR),
		],
	),
	user(
		{
			id: 'usr-022',
			name: 'André Luiz Barros',
			email: 'andre.barros@bancolitoral.coop.br',
			image: null,
		},
		'ACTIVE',
		'2026-03-03T12:00:00.000Z',
		contexts(
			[
				clientSetupIdOf('litoral', 'tributario', 'cadastro'),
				clientSetupIdOf('litoral', 'civel', 'basilisk'),
			],
			ROLE.BUSINESS_USER,
		),
	),
	user(
		{
			id: 'usr-023',
			name: 'Vanessa Cardoso',
			email: 'vanessa.cardoso@bancolitoral.coop.br',
			image: null,
		},
		'ACTIVE',
		'2026-04-16T12:00:00.000Z',
		contexts([clientSetupIdOf('litoral', 'civel', 'basilisk')], ROLE.OPERATOR),
	),
	user(
		{
			id: 'usr-024',
			name: 'Eduardo Pires',
			email: 'eduardo.pires@hoteismaresol.com.br',
			image: null,
		},
		'INACTIVE',
		'2026-03-21T12:00:00.000Z',
		contexts([clientSetupIdOf('maresol', 'trabalhista', 'cadastro')], ROLE.BUSINESS_USER),
	),
	user(
		{
			id: 'usr-025',
			name: 'Aline Freitas',
			email: 'aline.freitas@institutoipe.edu.br',
			image: null,
		},
		'ACTIVE',
		'2026-04-07T12:00:00.000Z',
		contexts(
			[
				clientSetupIdOf('ipe', 'trabalhista', 'cadastro'),
				clientSetupIdOf('ipe', 'trabalhista', 'contestacao'),
			],
			ROLE.BUSINESS_USER,
		),
	),
	user(
		{
			id: 'usr-026',
			name: 'Henrique Duarte',
			email: 'henrique.duarte@energiasolaris.com.br',
			image: null,
		},
		'ACTIVE',
		'2026-05-05T12:00:00.000Z',
		contexts(
			[
				clientSetupIdOf('solaris', 'tributario', 'cadastro'),
				clientSetupIdOf('solaris', 'civel', 'basilisk'),
			],
			ROLE.BUSINESS_USER,
		),
	),
	user(
		{
			id: 'usr-027',
			name: 'Tatiane Ramos',
			email: 'tatiane.ramos@energiasolaris.com.br',
			image: null,
		},
		'BLOCKED',
		'2026-05-28T12:00:00.000Z',
		contexts([clientSetupIdOf('solaris', 'tributario', 'cadastro')], ROLE.OPERATOR),
		{ lockedAt: '2026-09-26T08:55:00.000Z' },
	),
	user(
		{
			id: 'usr-028',
			name: 'Sérgio Monteiro',
			email: 'sergio.monteiro@campoverde.ind.br',
			image: null,
		},
		'ACTIVE',
		'2026-05-26T12:00:00.000Z',
		contexts(
			[
				clientSetupIdOf('campoverde', 'trabalhista', 'cadastro'),
				clientSetupIdOf('campoverde', 'trabalhista', 'contestacao'),
				clientSetupIdOf('campoverde', 'trabalhista', 'basilisk'),
			],
			ROLE.BUSINESS_USER,
		),
	),
	user(
		{
			id: 'usr-029',
			name: 'Daniela Cunha',
			email: 'daniela.cunha@campoverde.ind.br',
			image: null,
		},
		'ACTIVE',
		'2026-06-04T12:00:00.000Z',
		contexts(
			[
				clientSetupIdOf('campoverde', 'trabalhista', 'cadastro'),
				clientSetupIdOf('campoverde', 'trabalhista', 'contestacao'),
			],
			ROLE.OPERATOR,
		),
	),
	user(
		{
			id: 'usr-030',
			name: 'Otávio Brandão',
			email: 'otavio.brandao@campoverde.ind.br',
			image: null,
		},
		'INACTIVE',
		'2026-09-24T12:00:00.000Z',
		contexts([clientSetupIdOf('campoverde', 'trabalhista', 'basilisk')], ROLE.OPERATOR),
	),
	user(
		{
			id: 'usr-031',
			name: 'Larissa Vieira',
			email: 'larissa.vieira@seguradoraatlantica.com.br',
			image: null,
		},
		'ACTIVE',
		'2026-06-16T12:00:00.000Z',
		contexts(
			[
				clientSetupIdOf('atlantica', 'civel', 'basilisk'),
				clientSetupIdOf('atlantica', 'civel', 'cadastro'),
			],
			ROLE.BUSINESS_USER,
		),
	),
	user(
		{
			id: 'usr-032',
			name: 'Marcelo Antunes',
			email: 'marcelo.antunes@seguradoraatlantica.com.br',
			image: null,
		},
		'ACTIVE',
		'2026-06-22T12:00:00.000Z',
		contexts([clientSetupIdOf('atlantica', 'civel', 'basilisk')], ROLE.OPERATOR),
	),
	user(
		{
			id: 'usr-033',
			name: 'Priscila Rezende',
			email: 'priscila.rezende@rotasul.log.br',
			image: null,
		},
		'ACTIVE',
		'2026-07-09T12:00:00.000Z',
		contexts([clientSetupIdOf('rotasul', 'trabalhista', 'cadastro')], ROLE.BUSINESS_USER),
	),
	user(
		{
			id: 'usr-034',
			name: 'Vinícius Coelho',
			email: 'vinicius.coelho@rotasul.log.br',
			image: null,
		},
		'INACTIVE',
		'2026-09-12T12:00:00.000Z',
		contexts([clientSetupIdOf('rotasul', 'trabalhista', 'cadastro')], ROLE.OPERATOR),
	),
	user(
		{
			id: 'usr-035',
			name: 'Cláudia Seixas',
			email: 'claudia.seixas@lbca.com.br',
			image: null,
		},
		'ACTIVE',
		'2026-02-20T12:00:00.000Z',
		contexts(
			[
				clientSetupIdOf('horizonte', 'trabalhista', 'cadastro'),
				clientSetupIdOf('aurora', 'civel', 'cadastro'),
				clientSetupIdOf('campoverde', 'trabalhista', 'contestacao'),
			],
			ROLE.OPERATOR,
		),
	),
];

// ── Chaves de API ─────────────────────────────────────────────────────────────

export interface ApiKeyRecord extends Omit<ApiKeyListItem, 'product'> {
	productSetupId: string;
}

function apiKey(
	id: string,
	name: string,
	prefix: string,
	productSetupId: string,
	createdAt: string,
	extra: Partial<ApiKeyRecord> = {},
): ApiKeyRecord {
	return {
		id,
		name,
		prefix,
		productSetupId,
		expiresAt: null,
		lastUsedAt: null,
		createdAt,
		createdBy: MOCK_PEOPLE.gerente,
		updatedAt: createdAt,
		updatedBy: MOCK_PEOPLE.gerente,
		deletedAt: null,
		deletedBy: null,
		...extra,
	};
}

export const apiKeysDb: ApiKeyRecord[] = [
	apiKey(
		'key-001',
		'Integração ERP Protheus',
		'lbca_live_8f3a',
		ACME_ALL[0],
		'2026-02-01T10:00:00.000Z',
		{
			expiresAt: '2027-02-01T10:00:00.000Z',
			lastUsedAt: '2026-09-23T21:14:00.000Z',
		},
	),
	apiKey(
		'key-002',
		'Robô de captura de processos',
		'lbca_live_c27d',
		ACME_ALL[0],
		'2026-05-12T10:00:00.000Z',
		{
			expiresAt: '2026-10-05T10:00:00.000Z',
			lastUsedAt: '2026-09-22T08:03:00.000Z',
		},
	),
	apiKey(
		'key-003',
		'Ambiente de homologação',
		'lbca_test_19be',
		ACME_ALL[0],
		'2026-01-20T10:00:00.000Z',
		{
			deletedAt: '2026-06-30T17:00:00.000Z',
			deletedBy: MOCK_PEOPLE.admin,
		},
	),
	apiKey(
		'key-004',
		'Sistema jurídico interno',
		'lbca_live_5e01',
		ACME_ALL[1],
		'2026-03-03T10:00:00.000Z',
		{
			lastUsedAt: '2026-09-20T16:45:00.000Z',
		},
	),
	apiKey(
		'key-005',
		'Esteira de auditoria',
		'lbca_live_a4f9',
		ACME_ALL[2],
		'2026-04-25T10:00:00.000Z',
		{
			expiresAt: '2026-08-31T23:59:59.000Z',
			lastUsedAt: '2026-08-30T11:20:00.000Z',
		},
	),
	// ── Chaves dos clientes fictícios ──
	apiKey(
		'key-006',
		'Integração prontuário RH',
		'lbca_live_7b2e',
		clientSetupIdOf('horizonte', 'trabalhista', 'cadastro'),
		'2025-10-28T10:00:00.000Z',
		{
			expiresAt: '2027-04-30T23:59:59.000Z',
			lastUsedAt: '2026-09-28T19:32:00.000Z',
		},
	),
	apiKey(
		'key-007',
		'Conector plataforma e-commerce',
		'lbca_live_d91c',
		clientSetupIdOf('aurora', 'civel', 'cadastro'),
		'2026-01-12T10:00:00.000Z',
		{
			expiresAt: '2026-10-12T23:59:59.000Z',
			lastUsedAt: '2026-09-29T07:48:00.000Z',
		},
	),
	apiKey(
		'key-008',
		'Chave legada (piloto)',
		'lbca_live_02fa',
		clientSetupIdOf('aurora', 'civel', 'cadastro'),
		'2025-12-16T10:00:00.000Z',
		{
			lastUsedAt: '2026-01-11T17:05:00.000Z',
			deletedAt: '2026-01-12T10:05:00.000Z',
			deletedBy: MOCK_PEOPLE.admin,
		},
	),
	apiKey(
		'key-009',
		'ERP Campo Verde',
		'lbca_live_6ce4',
		clientSetupIdOf('campoverde', 'trabalhista', 'cadastro'),
		'2026-05-27T10:00:00.000Z',
		{
			lastUsedAt: '2026-09-29T09:12:00.000Z',
		},
	),
	apiKey(
		'key-010',
		'Robô jurídico — contestações',
		'lbca_live_b35d',
		clientSetupIdOf('campoverde', 'trabalhista', 'contestacao'),
		'2026-06-05T10:00:00.000Z',
		{
			expiresAt: '2027-06-05T10:00:00.000Z',
			lastUsedAt: '2026-09-27T15:40:00.000Z',
		},
	),
	apiKey(
		'key-011',
		'Esteira antifraude',
		'lbca_live_4a80',
		clientSetupIdOf('litoral', 'civel', 'basilisk'),
		'2026-04-20T10:00:00.000Z',
		{
			expiresAt: '2027-01-31T23:59:59.000Z',
			lastUsedAt: '2026-09-28T22:18:00.000Z',
		},
	),
	apiKey(
		'key-012',
		'Sinistros — análise documental',
		'lbca_live_e7f3',
		clientSetupIdOf('atlantica', 'civel', 'basilisk'),
		'2026-06-18T10:00:00.000Z',
		{
			expiresAt: '2026-10-20T23:59:59.000Z',
			lastUsedAt: '2026-09-29T08:30:00.000Z',
		},
	),
	apiKey(
		'key-013',
		'Auditoria de contratos',
		'lbca_live_91ad',
		clientSetupIdOf('solaris', 'civel', 'basilisk'),
		'2026-05-29T10:00:00.000Z',
		{
			expiresAt: '2026-09-15T23:59:59.000Z',
			lastUsedAt: '2026-09-15T18:02:00.000Z',
		},
	),
	apiKey(
		'key-014',
		'Portal da ouvidoria',
		'lbca_live_58b7',
		clientSetupIdOf('pampa', 'civel', 'basilisk'),
		'2026-02-03T10:00:00.000Z',
		{
			lastUsedAt: '2026-09-26T13:44:00.000Z',
		},
	),
	apiKey(
		'key-015',
		'Ambiente de homologação',
		'lbca_test_3e6a',
		clientSetupIdOf('valenorte', 'trabalhista', 'contestacao'),
		'2025-12-05T10:00:00.000Z',
		{
			lastUsedAt: '2026-02-14T10:21:00.000Z',
			deletedAt: '2026-03-01T09:00:00.000Z',
			deletedBy: MOCK_PEOPLE.gerente,
		},
	),
	apiKey(
		'key-016',
		'Integração reservas RH',
		'lbca_live_c0d2',
		clientSetupIdOf('maresol', 'trabalhista', 'cadastro'),
		'2026-03-25T10:00:00.000Z',
		{
			lastUsedAt: '2026-07-30T20:10:00.000Z',
			deletedAt: '2026-08-14T10:00:00.000Z',
			deletedBy: MOCK_PEOPLE.admin,
		},
	),
	apiKey(
		'key-017',
		'Integração folha de pagamento',
		'lbca_live_a7c9',
		clientSetupIdOf('ipe', 'trabalhista', 'cadastro'),
		'2026-04-10T10:00:00.000Z',
		{
			expiresAt: '2027-04-10T10:00:00.000Z',
			lastUsedAt: '2026-09-24T11:05:00.000Z',
		},
	),
];
